const CHUNK_LOAD_ERROR_MESSAGES = [
  // Chromium
  'Failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // WebKit
  'Importing a module script failed',
  // WebKit, for a chunk response without CORS headers (e.g. a CDN error page)
  'Cross-origin script load denied',
];

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * WebKit and Firefox cancel in-flight chunk requests as soon as a navigation
 * to another document starts, while the page is still visible. Retrying then
 * is pointless, the page is about to go away. A navigation that has not
 * replaced the page after this long was abandoned (a download, a link to an
 * external app, the stop button).
 */
const ABANDONED_NAVIGATION_TIMEOUT_MS = 5000;

let leavingPageSince: number | undefined;

if (process.browser) {
  const markLeavingPage = () => {
    leavingPageSince = Date.now();
  };

  const navigation = 'navigation' in window ? window.navigation : undefined;

  if (navigation) {
    navigation.addEventListener('navigate', event => {
      if (!event.destination.sameDocument && !event.downloadRequest) {
        markLeavingPage();
      }
    });
  } else {
    // Fallback for browsers without the Navigation API only: a beforeunload
    // listener keeps pages out of Firefox's back/forward cache.
    window.addEventListener('beforeunload', markLeavingPage);
  }

  window.addEventListener('pageshow', () => {
    leavingPageSince = undefined;
  });
}

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  CHUNK_LOAD_ERROR_MESSAGES.some(message => error.message.includes(message));

type ImportModule = () => Promise<unknown>;
type VitePreload = (
  importModule: ImportModule,
  deps?: string[],
  importerUrl?: string,
) => Promise<unknown>;

/**
 * Chrome and Safari keep a failed module fetch for the lifetime of the page:
 * importing the same URL again rejects without a request. A retry therefore
 * loads the chunk from a URL the page has not seen yet, and every later import
 * of the chunk uses that URL so the module is only ever evaluated once.
 */
const recoveredModuleUrls = new Map<string, string>();
const cacheBustedAttempts = new Map<string, number>();
const pendingRetries = new Map<string, Promise<string>>();

/**
 * Wraps Vite's preload helper, which `@aboutyou/bundler` puts around every
 * dynamic import of the client build.
 */
export const retryFailedChunkLoads =
  (vitePreload: VitePreload): VitePreload =>
  (importModule, deps, importerUrl) => {
    const moduleUrl = getModuleUrl(importModule, importerUrl);
    const recoveredModuleUrl = moduleUrl && recoveredModuleUrls.get(moduleUrl);

    if (recoveredModuleUrl) {
      return import(/* @vite-ignore */ recoveredModuleUrl);
    }

    return vitePreload(importModule, deps, importerUrl).catch(error => {
      if (!moduleUrl || !isChunkLoadError(error)) {
        throw error;
      }

      return findLoadableModuleUrl(moduleUrl, error).then(
        loadableModuleUrl => import(/* @vite-ignore */ loadableModuleUrl),
      );
    });
  };

/**
 * Vite passes the import as `() => import("./chunk-[hash].js")` and the URL
 * of the importing chunk, so the chunk URL is read from the function source.
 */
const getModuleUrl = (importModule: ImportModule, importerUrl?: string) => {
  const specifier = /import\((["'`])(.+?)\1\)/.exec(
    importModule.toString(),
  )?.[2];

  return specifier && importerUrl
    ? new URL(specifier, importerUrl).href
    : undefined;
};

const findLoadableModuleUrl = (moduleUrl: string, error: unknown) => {
  let pendingRetry = pendingRetries.get(moduleUrl);

  if (!pendingRetry) {
    pendingRetry = retryModuleUrl(moduleUrl, error).finally(() => {
      pendingRetries.delete(moduleUrl);
    });
    pendingRetries.set(moduleUrl, pendingRetry);
  }

  return pendingRetry;
};

const retryModuleUrl = async (moduleUrl: string, error: unknown) => {
  let lastError = error;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    await waitBeforeRetry(attempt);

    // Browsers following the current HTML spec (Firefox 155+) fetch a failed
    // module again, so the original URL is tried first.
    for (const url of [moduleUrl, getCacheBustedUrl(moduleUrl)]) {
      try {
        await import(/* @vite-ignore */ url);
        recoveredModuleUrls.set(moduleUrl, url);
        return url;
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) {
          throw retryError;
        }
        lastError = retryError;
      }
    }
  }

  throw lastError;
};

const getCacheBustedUrl = (moduleUrl: string) => {
  const attempt = (cacheBustedAttempts.get(moduleUrl) ?? 0) + 1;
  cacheBustedAttempts.set(moduleUrl, attempt);

  const url = new URL(moduleUrl);
  url.searchParams.set('retry', String(attempt));
  return url.href;
};

const waitBeforeRetry = async (attempt: number) => {
  await new Promise(resolve => {
    setTimeout(resolve, RETRY_DELAY_MS * attempt);
  });
  await waitUntilPageCanLoadChunks();
};

const getLeavingPageTimeLeft = () =>
  leavingPageSince === undefined
    ? 0
    : leavingPageSince + ABANDONED_NAVIGATION_TIMEOUT_MS - Date.now();

const canLoadChunks = () =>
  document.visibilityState === 'visible' &&
  navigator.onLine &&
  getLeavingPageTimeLeft() <= 0;

const waitUntilPageCanLoadChunks = () =>
  new Promise<void>(resolve => {
    let abandonedNavigationTimer: number | undefined;

    const resolveOnceChunksCanLoad = () => {
      window.clearTimeout(abandonedNavigationTimer);

      if (!canLoadChunks()) {
        const leavingPageTimeLeft = getLeavingPageTimeLeft();
        if (leavingPageTimeLeft > 0) {
          abandonedNavigationTimer = window.setTimeout(
            resolveOnceChunksCanLoad,
            leavingPageTimeLeft,
          );
        }
        return;
      }

      document.removeEventListener(
        'visibilitychange',
        resolveOnceChunksCanLoad,
      );
      window.removeEventListener('online', resolveOnceChunksCanLoad);
      window.removeEventListener('pageshow', resolveOnceChunksCanLoad);
      resolve();
    };

    document.addEventListener('visibilitychange', resolveOnceChunksCanLoad);
    window.addEventListener('online', resolveOnceChunksCanLoad);
    window.addEventListener('pageshow', resolveOnceChunksCanLoad);
    resolveOnceChunksCanLoad();
  });
